import { useState, useEffect } from 'react';
import { attendanceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Clock, TrendingUp, TrendingDown, Minus, Calendar, AlertTriangle, CheckCircle, Sparkles,
  FileEdit, Send, Bell
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { toast } from 'sonner';

export default function AttendanceStatsWidget({ employeeId, compact = false }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Today's digest
  const [todayDigest, setTodayDigest] = useState(null);
  
  // Regularization request
  const [showRegularizationDialog, setShowRegularizationDialog] = useState(false);
  const [regularizationSubmitting, setRegularizationSubmitting] = useState(false);
  const [regularizationForm, setRegularizationForm] = useState({
    request_type: 'single_punch',
    date: '',
    reason: '',
    proposed_check_in: '',
    proposed_check_out: ''
  });

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = employeeId 
          ? await attendanceAPI.getAIStats(employeeId, 30)
          : await attendanceAPI.getMyAIStats(30);
        setStats(res.data);
        
        // Fetch today's digest only for current user
        if (!employeeId) {
          try {
            const digestRes = await attendanceAPI.getTodayDigest();
            setTodayDigest(digestRes.data);
          } catch (digestErr) {
            console.error('Failed to fetch digest:', digestErr);
          }
        }
      } catch (err) {
        console.error('Failed to fetch attendance stats:', err);
        setError('Failed to load attendance data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [employeeId]);
  
  const handleSubmitRegularization = async () => {
    if (!regularizationForm.date || !regularizationForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setRegularizationSubmitting(true);
    try {
      await attendanceAPI.createRegularizationRequest(regularizationForm);
      toast.success('Regularization request submitted successfully');
      setShowRegularizationDialog(false);
      setRegularizationForm({
        request_type: 'single_punch',
        date: '',
        reason: '',
        proposed_check_in: '',
        proposed_check_out: ''
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setRegularizationSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !stats?.has_data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Calendar className="h-12 w-12 mb-4 opacity-50" />
          <p>No attendance data available</p>
          <p className="text-sm">Data will appear after attendance import</p>
        </CardContent>
      </Card>
    );
  }

  const { summary, deductions, trend, insights, weekly_data } = stats;

  const TrendIcon = trend.direction === 'improving' 
    ? TrendingUp 
    : trend.direction === 'declining' 
      ? TrendingDown 
      : Minus;

  const trendColor = trend.direction === 'improving' 
    ? 'text-green-500' 
    : trend.direction === 'declining' 
      ? 'text-red-500' 
      : 'text-gray-500';

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Attendance Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold">{summary.attendance_rate}%</span>
            <Badge variant={summary.attendance_rate >= 80 ? 'default' : 'destructive'}>
              {summary.full_days} full days
            </Badge>
          </div>
          <Progress value={summary.attendance_rate} className="h-2" />
          <div className="flex items-center gap-1 text-sm">
            <TrendIcon className={`h-4 w-4 ${trendColor}`} />
            <span className={trendColor}>
              {trend.direction === 'improving' ? '+' : ''}{trend.change}h avg
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Stats Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Attendance Insights
          </CardTitle>
          <CardDescription>Last 30 days performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Attendance Rate */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-primary">{summary.attendance_rate}%</div>
              <div className="text-sm text-muted-foreground">Attendance Rate</div>
              <Progress value={summary.attendance_rate} className="h-1 mt-2" />
            </div>
            
            {/* Punctuality */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-emerald-600">{summary.punctuality_score}%</div>
              <div className="text-sm text-muted-foreground">Punctuality</div>
              <Progress value={summary.punctuality_score} className="h-1 mt-2" />
            </div>
            
            {/* Avg Hours */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{summary.avg_hours_per_day}h</div>
              <div className="text-sm text-muted-foreground">Avg Hours/Day</div>
            </div>
            
            {/* Trend */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className={`text-3xl font-bold flex items-center justify-center gap-1 ${trendColor}`}>
                <TrendIcon className="h-6 w-6" />
                {Math.abs(trend.change)}h
              </div>
              <div className="text-sm text-muted-foreground capitalize">{trend.direction}</div>
            </div>
          </div>

          {/* Day Breakdown */}
          <div className="grid grid-cols-5 gap-2 mb-6">
            <div className="text-center p-2 bg-green-50 rounded border border-green-200">
              <div className="text-xl font-bold text-green-600">{summary.full_days}</div>
              <div className="text-xs text-green-700">Full Days</div>
            </div>
            <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
              <div className="text-xl font-bold text-yellow-600">{summary.half_days}</div>
              <div className="text-xs text-yellow-700">Half Days</div>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded border border-orange-200">
              <div className="text-xl font-bold text-orange-600">{summary.short_days}</div>
              <div className="text-xs text-orange-700">Short Days</div>
            </div>
            <div className="text-center p-2 bg-red-50 rounded border border-red-200">
              <div className="text-xl font-bold text-red-600">{summary.single_punch_days || 0}</div>
              <div className="text-xs text-red-700">Single Punch</div>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
              <div className="text-xl font-bold text-blue-600">{summary.total_hours}</div>
              <div className="text-xs text-blue-700">Total Hours</div>
            </div>
          </div>
          
          {/* Single Punch Alert */}
          {summary.single_punch_days > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-red-800">
                  {summary.single_punch_days} Single Punch Day{summary.single_punch_days > 1 ? 's' : ''}
                </div>
                <div className="text-sm text-red-700">
                  Days with missing check-in or check-out. Use the regularization request below to correct these.
                </div>
              </div>
            </div>
          )}

          {/* Deductions Alert */}
          {deductions.active > 0 && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-orange-800">
                  {deductions.active} Active Deduction{deductions.active > 1 ? 's' : ''}
                </div>
                <div className="text-sm text-orange-700">
                  You can compensate by working 4 extra hours per half-day. 
                  {deductions.reversed > 0 && ` (${deductions.reversed} already reversed)`}
                </div>
              </div>
            </div>
          )}

          {deductions.active === 0 && deductions.reversed > 0 && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-green-800">All Clear!</div>
                <div className="text-sm text-green-700">
                  {deductions.reversed} deduction{deductions.reversed > 1 ? 's were' : ' was'} reversed through compensation.
                </div>
              </div>
            </div>
          )}

          {/* AI Insights */}
          {insights && insights.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                AI Insights
              </h4>
              <div className="space-y-2">
                {insights.map((insight, idx) => (
                  <div key={idx} className="text-sm p-3 bg-muted rounded-lg">
                    {insight}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Chart */}
      {weekly_data && weekly_data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Weekly Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    formatter={(value, name) => [
                      name === 'total_hours' ? `${value}h` : value,
                      name === 'total_hours' ? 'Hours' : 'Full Days'
                    ]}
                  />
                  <Bar dataKey="total_hours" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Today's Digest Alert */}
      {todayDigest && todayDigest.issues && todayDigest.issues.length > 0 && !employeeId && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
              <Bell className="h-5 w-5" />
              Today's Attendance Alert
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayDigest.issues.map((issue, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border ${
                  issue.severity === 'high' ? 'bg-red-50 border-red-200' : 
                  issue.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' : 
                  'bg-gray-50 border-gray-200'
                }`}
              >
                <div className={`font-medium ${
                  issue.severity === 'high' ? 'text-red-800' : 
                  issue.severity === 'medium' ? 'text-yellow-800' : 'text-gray-800'
                }`}>
                  {issue.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div className="text-sm text-muted-foreground">{issue.message}</div>
              </div>
            ))}
            
            <Button 
              variant="outline" 
              className="w-full mt-2" 
              onClick={() => setShowRegularizationDialog(true)}
            >
              <FileEdit className="h-4 w-4 mr-2" />
              Request Regularization
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Regularization Request Button (for non-compact view) */}
      {!employeeId && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowRegularizationDialog(true)}
          >
            <FileEdit className="h-4 w-4 mr-2" />
            Request Attendance Regularization
          </Button>
        </div>
      )}
      
      {/* Regularization Dialog */}
      <Dialog open={showRegularizationDialog} onOpenChange={setShowRegularizationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Attendance Regularization</DialogTitle>
            <DialogDescription>
              Submit a request to regularize missing or incorrect attendance.
              This will go through HR and Super Admin approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <Select
                value={regularizationForm.request_type}
                onValueChange={(val) => setRegularizationForm(prev => ({ ...prev, request_type: val }))}
              >
                <SelectTrigger data-testid="regularization-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_punch">Missing Punch (Single Swipe)</SelectItem>
                  <SelectItem value="missing_attendance">Missing Attendance (Forgot to Punch)</SelectItem>
                  <SelectItem value="wrong_punch">Wrong Punch Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={regularizationForm.date}
                onChange={(e) => setRegularizationForm(prev => ({ ...prev, date: e.target.value }))}
                data-testid="regularization-date-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proposed Check-in</Label>
                <Input
                  type="time"
                  value={regularizationForm.proposed_check_in}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, proposed_check_in: e.target.value }))}
                  data-testid="regularization-checkin-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Proposed Check-out</Label>
                <Input
                  type="time"
                  value={regularizationForm.proposed_check_out}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, proposed_check_out: e.target.value }))}
                  data-testid="regularization-checkout-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                placeholder="Explain why you need attendance regularization..."
                value={regularizationForm.reason}
                onChange={(e) => setRegularizationForm(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                data-testid="regularization-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegularizationDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRegularization} 
              disabled={regularizationSubmitting}
              data-testid="regularization-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {regularizationSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
